import { Typography } from 'antd'
import { observer } from 'mobx-react-lite'

import { formTemplateWithBlocks } from '@/Ship/Templates/Form/formTemplateWithBlocks'

import { printableProviderOf, templateSyntaxExamples } from '../Helpers/templateSyntaxExamples'

interface TemplateSyntaxProps {
    formContext: unknown
}

interface TemplateFormValues {
    uuid?: unknown
    provider?: unknown
}

const valuesOf = (formContext: unknown): TemplateFormValues =>
    (formContext as { values?: TemplateFormValues } | null)?.values ?? {}

/**
 * Довідка про змінні шаблону в картці типу документа, над полем «Шаблон»
 * (`SPEC-012` §9.1, §13).
 *
 * Постійний блок, а не підказка `Hint` (вичитка підказок 2026-10-14, №24):
 * це єдине місце, де описано синтаксис змінних, а підказку можна сховати —
 * і тоді верстальник лишався без довідки, бо помилка 422 називає лише
 * невідомі змінні. `id` колишньої підказки `settings.document-types.template`
 * більше не вживається.
 *
 * Приклади — за провайдером типу (`client.*` лише в шаблоні клієнта,
 * `carrier.*` — перевізника): спільний приклад `${client.fullName}` у
 * шаблоні перевізника був невідомою змінною. Повного переліку змінних тут
 * немає: каталог (`VariableCatalog`) живе лише на беку, і API його не віддає.
 *
 * Поле «Шаблон» є лише в збереженому типі (`depFn` ресурсу, P-05), тож і
 * довідка — лише там: у формі створення вона пояснювала б поле, якого немає.
 * Провайдер читається з поточного значення форми: шаблон звіряється саме з
 * ним, зокрема коли провайдер змінюють за вже завантаженого шаблону
 * (`TemplateValidator::assertKnownVariables`).
 */
export const DocumentTypeTemplateSyntax = observer(({ formContext }: TemplateSyntaxProps) => {
    const values = valuesOf(formContext)

    if (typeof values.uuid !== 'string' || values.uuid === '') return null

    const provider = printableProviderOf(values.provider)
    const examples = templateSyntaxExamples(provider)

    return (
        <div data-testid="document-type-template-syntax" style={{ marginBottom: 16 }}>
            <Typography.Text strong>Змінні шаблону</Typography.Text>
            <Typography.Paragraph type="secondary" style={{ margin: '4px 0 0' }}>
                У шаблоні .docx змінна пишеться як <Typography.Text code>{'${ключ}'}</Typography.Text>.{' '}
                {provider === null ? (
                    <>
                        Ключі клієнта починаються з <Typography.Text code>client.</Typography.Text>, перевізника — з{' '}
                        <Typography.Text code>carrier.</Typography.Text>
                    </>
                ) : provider.value === 'application' ? (
                    <>
                        Ключі заявки починаються з <Typography.Text code>application.</Typography.Text>, її сторін — з{' '}
                        <Typography.Text code>client.</Typography.Text> і{' '}
                        <Typography.Text code>carrier.</Typography.Text>, їхніх договорів — з{' '}
                        <Typography.Text code>clientContract.</Typography.Text> і{' '}
                        <Typography.Text code>carrierContract.</Typography.Text>; номер і дата договору з форми —{' '}
                        <Typography.Text code>contractNumber</Typography.Text> і{' '}
                        <Typography.Text code>contractDate</Typography.Text>
                    </>
                ) : (
                    <>
                        Ключі провайдера «{provider.label}» починаються з{' '}
                        <Typography.Text code>{`${provider.value}.`}</Typography.Text>
                    </>
                )}
                , юрособи — з <Typography.Text code>legalEntity.</Typography.Text>, номер і дата документа —{' '}
                <Typography.Text code>number</Typography.Text> і <Typography.Text code>date</Typography.Text>.
            </Typography.Paragraph>
            <ul style={{ margin: '8px 0', paddingLeft: 20 }}>
                {examples.map((example) => (
                    <li key={example.macro}>
                        <Typography.Text code copyable={{ text: example.macro }}>
                            {example.macro}
                        </Typography.Text>{' '}
                        — {example.meaning}
                    </li>
                ))}
            </ul>
            <Typography.Text type="secondary">
                {provider === null
                    ? 'Шаблон зі змінною, якої немає в провайдера типу, не завантажиться.'
                    : `Шаблон зі змінною, якої немає в провайдера «${provider.label}», не завантажиться.`}
            </Typography.Text>
        </div>
    )
})

/** Форма типу документа: ядрова форма + довідка про змінні над «Шаблоном». */
export const documentTypeFormTemplate = formTemplateWithBlocks({ template: DocumentTypeTemplateSyntax })
